import {
  setLevel,
  setLevels,
  setLevelSearchResults,
  setLevelsForOrganisation,
} from "reduxStore/state";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export const createLevel = (values) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const createdLevelResponse = await fetch(`${BASE_URL}/levels`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(values),
    });

    if (!createdLevelResponse.ok) {
      throw new Error("Failed to create level");
    }

    const createdLevel = await createdLevelResponse.json();

    dispatch(setLevel(createdLevel));
    dispatch(
      fetchLevels({ page: 0, limit: 100, sort: "", search: "", location: "" }),
    );

    return createdLevel;
  } catch (error) {
    console.error("Error while creating level:", error);
    return null;
  }
};

export const updateLevel = (levelId, values) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const updatedLevelResponse = await fetch(`${BASE_URL}/levels/${levelId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(values),
    });

    if (!updatedLevelResponse.ok) {
      throw new Error("Failed to update level");
    }

    const updatedLevel = await updatedLevelResponse.json();

    dispatch(setLevel(updatedLevel));
    dispatch(
      fetchLevels({ page: 0, limit: 100, sort: "", search: "", location: "" }),
    );

    return updatedLevel;
  } catch (error) {
    console.error("Error while updating level:", error);
    return null;
  }
};

export const fetchLevels =
  ({ page = 0, limit = 10, sort, search, location } = {}) =>
  async (dispatch, getState) => {
    try {
      const token = getState().token;

      const query = new URLSearchParams({
        page: Number(page), // Ensure it's a number
        limit,
        sort,
        search,
        location,
      }).toString();

      const response = await fetch(`${BASE_URL}/levels?${query}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch levels");
      }

      const data = await response.json();

      dispatch(setLevels({ data: data.data, total: data.total }));

      return data;
    } catch (error) {
      console.error("Error while fetching levels:", error);
      return null;
    }
  };

export const fetchLevelById = (levelId) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const levelResponse = await fetch(`${BASE_URL}/levels/${levelId}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!levelResponse.ok) {
      throw new Error("Failed to fetch level");
    }

    const level = await levelResponse.json();

    dispatch(setLevel(level));

    return level;
  } catch (error) {
    console.error("Error while fetching level:", error);
    return null;
  }
};

export const fetchLevelsForOrganisation =
  ({ organisationId, locationId }) =>
  async (dispatch, getState) => {
    try {
      const token = getState().token;

      const query = new URLSearchParams({
        organisationId,
        locationId,
      }).toString();

      const response = await fetch(`${BASE_URL}/levels/organisation?${query}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch levels for organisation");
      }

      const data = await response.json();

      dispatch(setLevelsForOrganisation(data));

      return data;
    } catch (error) {
      console.error("Error while fetching levels for organisation:", error);
      return null;
    }
  };

export const searchLevels =
  (search, location) => async (dispatch, getState) => {
    try {
      const token = getState().token;

      const response = await fetch(
        `${BASE_URL}/levels/search?search=${search}&location=${location}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to search levels");
      }

      const data = await response.json();

      dispatch(setLevelSearchResults(data));

      return data;
    } catch (error) {
      console.error("Error while searching levels:", error);
      return null;
    }
  };

export const fetchLevelsForLocation =
  (locationId) => async (dispatch, getState) => {
    try {
      const token = getState().token;

      const response = await fetch(
        `${BASE_URL}/levels/location/${locationId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch levels for location");
      }

      const data = await response.json();

      dispatch(setLevels({ data: data.data, total: data.total }));

      return data;
    } catch (error) {
      console.error("Error while fetching levels for location:", error);
      return null;
    }
  };

export const deleteLevel = (levelId) => async (dispatch, getState) => {
  try {
    const token = getState().token;

    const response = await fetch(`${BASE_URL}/levels/${levelId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const responseData = await response.json();

    if (!response.ok || !responseData) throw new Error(responseData.message);

    dispatch(
      fetchLevels({ page: 0, limit: 100, sort: "", search: "", location: "" }),
    );

    return { success: true, message: responseData.message };
  } catch (error) {
    console.error("Error while deleting level:", error);

    // Detect if the error is due to associated assessments
    if (
      error.message.includes(
        "Cannot delete Year as it is associated with assessments",
      )
    ) {
      return { reassignmentRequired: true, message: error.message };
    }

    return {
      error: true,
      success: false,
      message: error.message,
    };
  }
};

// Reassign assessments from one level to another
export const reassignAssessments =
  (oldLevelId, newLevelId) => async (dispatch, getState) => {
    try {
      const token = getState().token;

      const response = await fetch(`${BASE_URL}/levels/reassign-assessments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ oldLevelId, newLevelId }),
      });

      if (!response.ok) {
        throw new Error("Failed to reassign assessments");
      }

      const data = await response.json();
      return { success: true, message: data.message };
    } catch (error) {
      console.error("Error while reassigning assessments:", error);
      return { error: true, message: error.message };
    }
  };
